<template>
    <aside id="account-panel" class="default-sidebar account-panel" aria-label="Mon compte">
        <div class="account-panel-card" :class="{ 'account-panel-card-yservices': isYservices }">
            <div class="account-panel-glow account-panel-glow-primary"></div>
            <div class="account-panel-glow account-panel-glow-secondary"></div>

            <div class="account-panel-header">
                <button type="button" class="account-panel-close" aria-label="Fermer le panneau du compte" @click="$emit('close')">
                    <feather type="x" />
                </button>
                <div class="account-panel-avatar">
                    <span>{{ userInitials }}</span>
                    <i></i>
                </div>
                <div class="account-panel-identity">
                    <span>{{ $tl("labels.myAccount") }}</span>
                    <strong>{{ userDisplayName }}</strong>
                </div>
                <div class="account-panel-badge">Compte</div>
            </div>

            <ul class="account-panel-list">
                <template v-for="(entry, index) in entries">
                    <SidebarNavItem :key="index" v-if="typeof entry.display === 'undefined' || entry.display === true" :entry="entry" :user="user" />
                </template>
            </ul>

            <div class="account-panel-footer">
                <button data-test-id="btn-logout" type="button" class="account-panel-logout" @click="$emit('logout')">
                    <span class="account-panel-logout-label">
                        <feather type="log-out" />
                        <span>{{ logoutLabel }}</span>
                    </span>
                    <feather type="chevron-right" class="account-panel-logout-arrow" />
                </button>
            </div>
        </div>
    </aside>
</template>

<script>
import SidebarNavItem from "./SidebarNavItem.vue";

export default {
    name: "Sidebar",
    props: {
        entries: {
            default: () => [],
        },
        user: {
            required: true,
        },
    },
    computed: {
        userDisplayName() {
            const name = [this.user.firstname, this.user.lastname].filter((part) => typeof part === "string" && part.trim() !== "").join(" ");

            return name || this.user.email || this.$tl("labels.myAccount");
        },
        logoutLabel() {
            return this.$tl("labels.routes.disconnect") || "Déconnexion";
        },
        userInitials() {
            const names = [this.user.firstname, this.user.lastname].filter((part) => typeof part === "string" && part.trim() !== "");

            if (names.length > 0) {
                return names
                    .slice(0, 2)
                    .map((part) => part.trim().charAt(0).toUpperCase())
                    .join("");
            }

            return this.userDisplayName.trim().charAt(0).toUpperCase();
        },
    },
    components: {
        SidebarNavItem,
    },
};
</script>

<style lang="scss" scoped>
.account-panel {
    height: 100%;
    max-width: 100%;
    overflow: hidden;
}

.account-panel-card {
    --account-panel-bg-start: #031c2e;
    --account-panel-bg-mid: #052a43;
    --account-panel-bg-end: #083e60;
    --account-panel-text: rgba(255, 255, 255, 0.94);
    --account-panel-muted: rgba(255, 255, 255, 0.62);
    --account-panel-border: rgba(255, 255, 255, 0.12);
    --account-panel-surface: rgba(255, 255, 255, 0.08);
    --account-panel-surface-strong: rgba(255, 255, 255, 0.14);
    --account-panel-highlight-primary: rgba(69, 156, 202, 0.36);
    --account-panel-highlight-secondary: rgba(255, 255, 255, 0.12);
    --account-panel-card-border: rgba(255, 255, 255, 0.16);
    --account-panel-card-shadow: 0 30px 80px rgba(2, 18, 30, 0.36), 0 14px 36px rgba(8, 62, 96, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.14);
    --account-panel-grid-line: rgba(255, 255, 255, 0.035);
    --account-panel-grid-line-alt: rgba(255, 255, 255, 0.025);
    --account-panel-glow-primary: rgba(71, 170, 224, 0.3);
    --account-panel-glow-secondary: rgba(255, 255, 255, 0.1);
    --account-panel-close-color: rgba(255, 255, 255, 0.76);
    --account-panel-close-color-hover: #ffffff;
    --account-panel-avatar-color: #ffffff;
    --account-panel-avatar-bg: linear-gradient(145deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.08));
    --account-panel-avatar-border: rgba(255, 255, 255, 0.24);
    --account-panel-avatar-shadow: 0 14px 30px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    --account-panel-avatar-status-border: #063252;
    --account-panel-strong: #ffffff;
    --account-panel-scrollbar: rgba(255, 255, 255, 0.22);
    --account-panel-scrollbar-hover: rgba(255, 255, 255, 0.34);
    --account-panel-footer-border: rgba(255, 255, 255, 0.12);
    --account-panel-logout-color: #ffffff;
    --account-panel-logout-hover-color: #ffdddd;
    --account-panel-logout-hover-bg: rgba(255, 82, 82, 0.16);
    --account-panel-logout-hover-border: rgba(255, 132, 132, 0.3);
    --account-panel-danger-icon: #ffb4b4;
    --account-panel-link-color: rgba(255, 255, 255, 0.78);
    --account-panel-link-hover-color: #ffffff;
    --account-panel-link-bg: rgba(255, 255, 255, 0.055);
    --account-panel-link-hover-bg: rgba(255, 255, 255, 0.12);
    --account-panel-link-active-bg: linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(69, 156, 202, 0.16));
    --account-panel-link-border: rgba(255, 255, 255, 0.08);
    --account-panel-link-hover-border: rgba(130, 202, 240, 0.32);
    --account-panel-link-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
    --account-panel-link-hover-shadow: 0 10px 24px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(130, 202, 240, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.12);
    --account-panel-link-visual-color: rgba(255, 255, 255, 0.76);
    --account-panel-link-visual-bg: rgba(255, 255, 255, 0.075);
    --account-panel-link-visual-hover-bg: rgba(130, 202, 240, 0.18);
    --account-panel-link-visual-hover-border: rgba(130, 202, 240, 0.24);
    --account-panel-link-visual-hover-shadow: 0 0 18px rgba(92, 181, 229, 0.2);
    --account-panel-link-icon-color: rgba(255, 255, 255, 0.42);
    --account-panel-link-icon-hover-color: rgba(255, 255, 255, 0.82);

    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 100%;
    padding: 16px;
    overflow: hidden;
    color: var(--account-panel-text);
    background: radial-gradient(circle at 16% 2%, var(--account-panel-highlight-primary), transparent 32%),
        radial-gradient(circle at 92% 18%, var(--account-panel-highlight-secondary), transparent 26%),
        linear-gradient(154deg, var(--account-panel-bg-start) 0%, var(--account-panel-bg-mid) 48%, var(--account-panel-bg-end) 100%);
    border: 1px solid var(--account-panel-card-border);
    border-radius: 16px 0 0 16px;
    box-shadow: var(--account-panel-card-shadow);
    backdrop-filter: blur(20px);
    isolation: isolate;
}

.account-panel-card-yservices {
    --account-panel-bg-start: #ffffff;
    --account-panel-bg-mid: #f8f9fa;
    --account-panel-bg-end: #eef0f2;
    --account-panel-text: #2f3437;
    --account-panel-muted: #68737a;
    --account-panel-border: rgba(231, 59, 33, 0.14);
    --account-panel-surface: rgba(255, 255, 255, 0.78);
    --account-panel-surface-strong: #ffffff;
    --account-panel-highlight-primary: rgba(231, 59, 33, 0.08);
    --account-panel-highlight-secondary: rgba(255, 255, 255, 0.74);
    --account-panel-card-border: rgba(231, 59, 33, 0.12);
    --account-panel-card-shadow: 0 26px 64px rgba(32, 39, 45, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.82);
    --account-panel-grid-line: rgba(231, 59, 33, 0.035);
    --account-panel-grid-line-alt: rgba(45, 52, 58, 0.025);
    --account-panel-glow-primary: rgba(231, 59, 33, 0.12);
    --account-panel-glow-secondary: rgba(255, 255, 255, 0.78);
    --account-panel-close-color: #5f686f;
    --account-panel-close-color-hover: #e73b21;
    --account-panel-avatar-color: #ffffff;
    --account-panel-avatar-bg: linear-gradient(145deg, #e73b21, #c92f19);
    --account-panel-avatar-border: rgba(231, 59, 33, 0.24);
    --account-panel-avatar-shadow: 0 14px 28px rgba(231, 59, 33, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.26);
    --account-panel-avatar-status-border: #f8f9fa;
    --account-panel-strong: #202529;
    --account-panel-scrollbar: rgba(231, 59, 33, 0.18);
    --account-panel-scrollbar-hover: rgba(231, 59, 33, 0.28);
    --account-panel-footer-border: rgba(231, 59, 33, 0.12);
    --account-panel-logout-color: #31383d;
    --account-panel-logout-hover-color: #e73b21;
    --account-panel-logout-hover-bg: rgba(231, 59, 33, 0.08);
    --account-panel-logout-hover-border: rgba(231, 59, 33, 0.22);
    --account-panel-danger-icon: #e73b21;
    --account-panel-link-color: #3b4247;
    --account-panel-link-hover-color: #202529;
    --account-panel-link-bg: rgba(255, 255, 255, 0.68);
    --account-panel-link-hover-bg: #ffffff;
    --account-panel-link-active-bg: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(231, 59, 33, 0.08));
    --account-panel-link-border: rgba(231, 59, 33, 0.1);
    --account-panel-link-hover-border: rgba(231, 59, 33, 0.22);
    --account-panel-link-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.84);
    --account-panel-link-hover-shadow: 0 10px 24px rgba(32, 39, 45, 0.08), 0 0 0 1px rgba(231, 59, 33, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.96);
    --account-panel-link-visual-color: #e73b21;
    --account-panel-link-visual-bg: rgba(231, 59, 33, 0.08);
    --account-panel-link-visual-hover-bg: rgba(231, 59, 33, 0.12);
    --account-panel-link-visual-hover-border: rgba(231, 59, 33, 0.2);
    --account-panel-link-visual-hover-shadow: 0 0 18px rgba(231, 59, 33, 0.12);
    --account-panel-link-icon-color: rgba(231, 59, 33, 0.46);
    --account-panel-link-icon-hover-color: #e73b21;
}

.account-panel-card::before {
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    content: "";
    background-image: linear-gradient(var(--account-panel-grid-line) 1px, transparent 1px),
        linear-gradient(90deg, var(--account-panel-grid-line-alt) 1px, transparent 1px);
    background-size: 34px 34px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.72), transparent 78%);
}

.account-panel-glow {
    position: absolute;
    z-index: -1;
    pointer-events: none;
    border-radius: 999px;
    filter: blur(28px);
}

.account-panel-glow-primary {
    top: -48px;
    right: -64px;
    width: 180px;
    height: 180px;
    background: var(--account-panel-glow-primary);
}

.account-panel-glow-secondary {
    bottom: 72px;
    left: -76px;
    width: 160px;
    height: 160px;
    background: var(--account-panel-glow-secondary);
}

.account-panel-close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    color: var(--account-panel-close-color);
    background: var(--account-panel-surface);
    border: 1px solid var(--account-panel-border);
    border-radius: 10px;
    transition: color 0.18s ease, background-color 0.18s ease;
}

.account-panel-close:hover,
.account-panel-close:focus {
    color: var(--account-panel-close-color-hover);
    background: var(--account-panel-surface-strong);
    outline: none;
}

.account-panel-close .feather {
    width: 15px;
    height: 15px;
}

.account-panel-header {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
    padding: 4px 4px 14px;
    margin-bottom: 10px;
    border-bottom: 1px solid var(--account-panel-border);
}

.account-panel-avatar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    color: var(--account-panel-avatar-color);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0;
    background: var(--account-panel-avatar-bg);
    border: 1px solid var(--account-panel-avatar-border);
    border-radius: 14px;
    box-shadow: var(--account-panel-avatar-shadow);
}

.account-panel-avatar span {
    position: relative;
    z-index: 1;
}

.account-panel-avatar i {
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 11px;
    height: 11px;
    background: #48d597;
    border: 2px solid var(--account-panel-avatar-status-border);
    border-radius: 999px;
    box-shadow: 0 0 16px rgba(72, 213, 151, 0.82);
}

.account-panel-identity {
    min-width: 0;
}

.account-panel-identity span,
.account-panel-badge {
    display: block;
    color: var(--account-panel-muted);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.account-panel-identity strong {
    display: block;
    overflow: hidden;
    color: var(--account-panel-strong);
    font-size: 14px;
    font-weight: 800;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.account-panel-badge {
    padding: 5px 8px;
    color: var(--account-panel-muted);
    background: var(--account-panel-surface);
    border: 1px solid var(--account-panel-border);
    border-radius: 999px;
}

.account-panel-list {
    flex: 1;
    min-height: 0;
    padding: 4px 2px 4px 0;
    margin: 0 -4px 0 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    list-style: none;
    scrollbar-color: var(--account-panel-scrollbar) transparent;
    scrollbar-width: thin;
}

.account-panel-list::-webkit-scrollbar {
    width: 5px;
}

.account-panel-list::-webkit-scrollbar-track {
    background: transparent;
}

.account-panel-list::-webkit-scrollbar-thumb {
    background: var(--account-panel-scrollbar);
    border-radius: 999px;
}

.account-panel-list::-webkit-scrollbar-thumb:hover {
    background: var(--account-panel-scrollbar-hover);
}

.account-panel-footer {
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    padding-top: 12px;
    margin-top: 12px;
    border-top: 1px solid var(--account-panel-footer-border);
}

.account-panel-logout {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 44px;
    padding: 0 12px 0 14px;
    color: var(--account-panel-logout-color);
    font-size: 13px;
    font-weight: 800;
    background: var(--account-panel-surface-strong);
    border: 1px solid var(--account-panel-border);
    border-radius: 12px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
    transition: color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
}

.account-panel-logout:hover {
    color: var(--account-panel-logout-hover-color);
    background: var(--account-panel-logout-hover-bg);
    border-color: var(--account-panel-logout-hover-border);
    transform: translateY(-1px);
}

.account-panel-logout-label {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
}

.account-panel-logout-label .feather,
.account-panel-logout-arrow {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    color: var(--account-panel-danger-icon);
}

.account-panel-logout-label span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 575px) {
    .account-panel-card {
        padding: 14px;
        border-radius: 14px 0 0 14px;
    }

    .account-panel-header {
        grid-template-columns: 40px minmax(0, 1fr);
    }

    .account-panel-avatar {
        width: 40px;
        height: 40px;
        border-radius: 13px;
    }

    .account-panel-badge {
        display: none;
    }
}
</style>
